import { useDisplayTimeZone } from '@/app-kit/timezone-context';

// Two canonical shapes:
//   date     → YYYY-MM-DD
//   datetime → YYYY-MM-DD HH:mm  (24h, no seconds)
// Legacy names are kept as aliases so existing call sites compile and render
// the canonical shape without per-site edits:
//   'short' | 'medium' | 'long'  → date
//   'datetime-short'             → datetime
type Format = 'date' | 'datetime' | 'short' | 'medium' | 'long' | 'datetime-short';

const DATETIME_FORMATS = new Set<Format>(['datetime', 'datetime-short']);

// A plain calendar date string (e.g. "2018-06-27") with no time component.
// Such values are dates, not instants, so they must NOT be timezone-converted
// — converting midnight backward into a western zone would shift the calendar
// day. (The API sends true date-only columns as `YYYY-MM-DD` text.)
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Renders a date/time in the app's active display timezone (from
 * `TimeZoneProvider` / the header selector). Pass an explicit `timeZone` to
 * override — e.g. a debug view that should always read UTC.
 */
export function FormatDate({
  date,
  format = 'date',
  timeZone,
}: {
  date: Date | string | number;
  format?: Format;
  timeZone?: string;
}) {
  const displayZone = useDisplayTimeZone();
  const zone = timeZone ?? displayZone;
  const withTime = DATETIME_FORMATS.has(format);
  return <span className="tabular-nums">{formatDateString(date, zone, withTime)}</span>;
}

/**
 * String form of `FormatDate`, for places that need a plain string rather than
 * a node — e.g. a `title=` tooltip. The caller supplies the zone (read it from
 * `useDisplayTimeZone()` at the component top).
 */
export function formatDateString(date: Date | string | number, timeZone: string, withTime: boolean): string {
  // Date-only string → render verbatim (already YYYY-MM-DD), no zone math.
  if (!withTime && typeof date === 'string' && DATE_ONLY_RE.test(date)) {
    return date;
  }
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return formatInZone(d, timeZone, withTime);
}

// YYYY-MM-DD [HH:mm] — year-first so it reads unambiguously in any locale.
// Assembled from parts because no locale ships exactly this shape.
function formatInZone(d: Date, timeZone: string, withTime: boolean): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  const ymd = `${get('year')}-${get('month')}-${get('day')}`;
  if (!withTime) return ymd;
  // hour12: false but Intl still emits "24" for midnight in some locales.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${ymd} ${hour}:${get('minute')}`;
}
